import { useEffect, useState, useCallback } from "react";
import { api, apiError, downloadFile } from "../lib/api";
import { useAuth, can } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, MagnifyingGlass, PencilSimple, Power, ClockCounterClockwise, Key, CheckCircle, MapPin, UsersThree, Trash, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", city: "", category: "General", gstin: "" };

export default function Partners() {
  const { user } = useAuth();
  const editable = can(user, ["admin", "ops"]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [loginPartner, setLoginPartner] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [manageP, setManageP] = useState(null);
  const [pinInput, setPinInput] = useState("");
  const [engs, setEngs] = useState([]);
  const [manageBusy, setManageBusy] = useState(false);
  const [walletP, setWalletP] = useState(null);
  const isAdmin = user?.role === "admin";

  const openManage = async (p) => {
    setManageP(p); setPinInput(""); setEngs([]);
    try { const r = await api.get(`/partners/${p.id}/engineers`); setEngs(r.data); } catch {}
  };
  const refreshManage = async (pid) => {
    const r = await api.get(`/partners/${pid}`); setManageP(r.data); load();
  };
  const addPincodes = async () => {
    const list = pinInput.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^\d{6}$/.test(s));
    if (list.length === 0) { toast.error("Enter one or more valid 6-digit pincodes"); return; }
    setManageBusy(true);
    try {
      await api.post(`/partners/${manageP.id}/pincodes`, { pincodes: list });
      toast.success(`${list.length} pincode(s) added`); setPinInput("");
      await refreshManage(manageP.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setManageBusy(false);
  };
  const downloadPinTemplate = async () => {
    try { await downloadFile("/partners/pincodes/import-template", "partner_pincodes_import_template.xlsx"); }
    catch { toast.error("Could not download template"); }
  };
  const importPincodes = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !manageP) return;
    setManageBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/partners/${manageP.id}/pincodes/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { added, skipped } = r.data;
      toast.success(`${added} pincode(s) imported${skipped ? `, ${skipped} skipped` : ""}`);
      await refreshManage(manageP.id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import failed"); }
    setManageBusy(false);
  };
  const removePincode = async (pin) => {
    const areas = (manageP.service_areas || [])
      .map((a) => ({ ...a, pincodes: (a.pincodes || []).filter((pc) => (pc.pincode || pc) !== pin) }))
      .filter((a) => (a.pincodes || []).length > 0);
    try {
      await api.put(`/partners/${manageP.id}/service-areas`, { service_areas: areas });
      toast.success("Pincode removed");
      await refreshManage(manageP.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    api.get("/partners", { params: { search, status: status === "all" ? "" : status } })
      .then((r) => setRows(r.data));
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.margin_value === "" || payload.margin_value === undefined || payload.margin_value === null) {
        payload.margin_value = null;
      } else {
        payload.margin_value = Number(payload.margin_value);
      }
      payload.service_job_limit = (payload.service_job_limit === "" || payload.service_job_limit == null) ? null : Number(payload.service_job_limit);
      payload.sell_pickup_limit = (payload.sell_pickup_limit === "" || payload.sell_pickup_limit == null) ? null : Number(payload.sell_pickup_limit);
      payload.sell_fee_value = payload.sell_fee_value === "" || payload.sell_fee_value == null ? 0 : Number(payload.sell_fee_value);
      payload.sell_low_balance_threshold = payload.sell_low_balance_threshold === "" || payload.sell_low_balance_threshold == null ? 0 : Number(payload.sell_low_balance_threshold);
      if (editId) await api.put(`/partners/${editId}`, payload);
      else await api.post("/partners", payload);
      toast.success(`Partner ${editId ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggle = async (p) => {
    try {
      await api.post(`/partners/${p.id}/toggle-status`);
      toast.success(`Partner ${p.status === "active" ? "disabled" : "enabled"}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openLogin = (p) => {
    setLoginForm({ email: p.login_email || p.email || "", password: "" });
    setLoginPartner(p);
  };

  const sendInvite = async () => {
    if (!loginForm.email) { toast.error("Email is required to send an invite"); return; }
    try {
      const { data } = await api.post(`/partners/${loginPartner.id}/invite`, { email: loginForm.email, origin: window.location.origin });
      toast.success(data.resent ? "Set-password link re-sent to partner" : "Invite sent — partner will set their password");
      setLoginPartner(null);
      setLoginForm({ email: "", password: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader
        title="Partners"
        subtitle="Manage service partners & activation"
        actions={editable && (
          <Button data-testid="add-partner-btn" onClick={openNew} className="rounded-sm gap-2">
            <Plus size={16} weight="bold" /> Add Partner
          </Button>
        )}
      />
      <div className="p-8 space-y-4">
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="partner-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search partners…" className="rounded-sm pl-9" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm w-40" data-testid="partner-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="partners-table">
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No partners found</TableCell></TableRow>
              )}
              {rows.map((p) => (
                <TableRow key={p.id} className="hover:bg-secondary/30">
                  <TableCell className="font-semibold">{p.name}</TableCell>
                  <TableCell className="text-sm">
                    <div>{p.contact_person || "—"}</div>
                    <div className="text-muted-foreground text-xs">{p.phone}</div>
                  </TableCell>
                  <TableCell>{p.city || "—"}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell className="font-mono text-xs">{p.gstin || "—"}</TableCell>
                  <TableCell><StatusBadge value={p.status} testid={`partner-status-${p.id}`} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowHistory(p)} data-testid={`partner-history-${p.id}`}>
                        <ClockCounterClockwise size={16} />
                      </Button>
                      {editable && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`partner-edit-${p.id}`}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => openManage(p)} data-testid={`partner-manage-${p.id}`} title="Pincodes & Engineers">
                            <MapPin size={16} />
                          </Button>
                          <Button variant="ghost" size="icon" className={`h-8 w-8 ${p.status === "active" ? "text-destructive" : "text-green-600"}`}
                            onClick={() => toggle(p)} data-testid={`partner-toggle-${p.id}`}>
                            <Power size={16} />
                          </Button>
                        </>
                      )}
                      {isAdmin && (
                        p.has_login ? (
                          <span className="inline-flex items-center gap-1" data-testid={`partner-has-login-${p.id}`}>
                            <span className="inline-flex items-center h-8 px-2 text-xs text-green-700 gap-1">
                              <CheckCircle size={14} weight="fill" /> Portal
                            </span>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => openLogin(p)} data-testid={`partner-resend-invite-${p.id}`} title="Resend set-password invite">
                              <Key size={15} />
                            </Button>
                          </span>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => openLogin(p)} data-testid={`partner-create-login-${p.id}`} title="Send login invite">
                            <Key size={16} />
                          </Button>
                        )
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="partner-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Partner" : "New Partner"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            {[
              ["name", "Partner Name", "QuickFix Services"],
              ["contact_person", "Contact Person", "Ravi Kumar"],
              ["email", "Email", "ravi@example.in"],
              ["phone", "Phone", "9876543210"],
              ["city", "City", "Bengaluru"],
              ["category", "Category", "Plumbing"],
              ["gstin", "GSTIN (optional)", "29ABCDE1234F1Z5"],
            ].map(([k, l, ph]) => (
              <div key={k}>
                <Label className="label-caps">{l}</Label>
                <Input data-testid={`partner-field-${k}`} value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  className="rounded-sm mt-1.5" />
              </div>
            ))}
            {isAdmin && (
              <div className="border-t border-border pt-4 space-y-3">
                <div>
                  <Label className="label-caps">Margin Override (confidential)</Label>
                  <p className="text-xs text-muted-foreground mt-1">Optional. When set, this overrides each rate card's platform fee for this partner. Leave blank to use rate card defaults.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-caps">Type</Label>
                    <Select value={form.margin_type || "percent"} onValueChange={(v) => setForm({ ...form, margin_type: v })}>
                      <SelectTrigger className="rounded-sm mt-1.5" data-testid="partner-field-margin-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percent (%)</SelectItem>
                        <SelectItem value="amount">Fixed (₹)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps">Value</Label>
                    <Input data-testid="partner-field-margin-value" type="number"
                      value={form.margin_value ?? ""}
                      onChange={(e) => setForm({ ...form, margin_value: e.target.value })}
                      placeholder={(form.margin_type || "percent") === "percent" ? "20" : "300"} className="rounded-sm mt-1.5" />
                  </div>
                </div>
              </div>
            )}

            {editable && (
              <div className="border-t border-border pt-4 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!form.offers_sell_devices}
                    onChange={(e) => setForm({ ...form, offers_sell_devices: e.target.checked })} data-testid="partner-offers-sell" />
                  Offers Sell Device pickups
                </label>
                <p className="text-xs text-muted-foreground">Enable to make this partner eligible for auto-assigned device pickups in their covered pincodes (where Sell Appliance is active).</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-caps">Service ticket limit</Label>
                    <Input type="number" min={1} value={form.service_job_limit ?? ""} placeholder="global default"
                      onChange={(e) => setForm({ ...form, service_job_limit: e.target.value })} className="rounded-sm mt-1.5" data-testid="partner-service-limit" />
                  </div>
                  <div>
                    <Label className="label-caps">Sell pickup limit</Label>
                    <Input type="number" min={1} value={form.sell_pickup_limit ?? ""} placeholder="global default"
                      onChange={(e) => setForm({ ...form, sell_pickup_limit: e.target.value })} className="rounded-sm mt-1.5" data-testid="partner-sell-limit" />
                  </div>
                </div>
                {form.offers_sell_devices && (
                  <div className="border border-border rounded-sm p-3 space-y-3 bg-secondary/30">
                    <p className="label-caps">Sell Deposit & Platform Fee</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-caps">Fee type</Label>
                        <Select value={form.sell_fee_type || "flat"} onValueChange={(v) => setForm({ ...form, sell_fee_type: v })}>
                          <SelectTrigger className="rounded-sm mt-1.5" data-testid="partner-sell-fee-type"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="flat">Flat ₹ / pickup</SelectItem><SelectItem value="percent">% of price</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="label-caps">Fee value</Label>
                        <Input type="number" value={form.sell_fee_value ?? ""} onChange={(e) => setForm({ ...form, sell_fee_value: e.target.value })} className="rounded-sm mt-1.5" data-testid="partner-sell-fee-value" />
                      </div>
                    </div>
                    <div>
                      <Label className="label-caps">Low balance threshold (₹)</Label>
                      <Input type="number" value={form.sell_low_balance_threshold ?? ""} onChange={(e) => setForm({ ...form, sell_low_balance_threshold: e.target.value })} className="rounded-sm mt-1.5" data-testid="partner-sell-threshold" />
                      <p className="text-xs text-muted-foreground mt-1">Auto-allocation pauses when deposit falls below this. Manage deposits from the partner's Wallet.</p>
                    </div>
                    {editId && <Button type="button" variant="outline" className="rounded-sm w-full" onClick={() => { setOpen(false); setWalletP({ ...form, id: editId }); }} data-testid="open-wallet-btn">Manage Deposit Wallet</Button>}
                  </div>
                )}
              </div>
            )}
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="partner-save">Save Partner</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!showHistory} onOpenChange={(o) => !o && setShowHistory(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">Status History — {showHistory?.name}</SheetTitle></SheetHeader>
          <div className="py-6 space-y-3" data-testid="partner-history-list">
            {(!showHistory?.history || showHistory.history.length === 0) && (
              <p className="text-sm text-muted-foreground">No status changes recorded.</p>
            )}
            {showHistory?.history?.slice().reverse().map((h, i) => (
              <div key={i} className="border border-border rounded-sm p-3 text-sm">
                <div className="flex items-center gap-2">
                  <StatusBadge value={h.from} /> → <StatusBadge value={h.to} />
                </div>
                <div className="text-xs text-muted-foreground mt-2">{h.by} · {new Date(h.at).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!loginPartner} onOpenChange={(o) => !o && setLoginPartner(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="partner-login-form">
          <SheetHeader><SheetTitle className="font-heading">{loginPartner?.has_login ? "Resend Login Invite" : "Send Login Invite"} — {loginPartner?.name}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <p className="text-sm text-muted-foreground">We'll email the partner a secure link to set their own password. Once set, they can sign in to the Partner Portal to view &amp; accept jobs. The link is valid for 7 days.</p>
            <div>
              <Label className="label-caps">Partner Email</Label>
              <Input data-testid="login-email-input" type="email" value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="partner@email.com" className="rounded-sm mt-1.5" />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setLoginPartner(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={sendInvite} data-testid="partner-login-save">{loginPartner?.has_login ? "Resend Invite" : "Send Invite"}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!manageP} onOpenChange={(o) => !o && setManageP(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="partner-manage-sheet">
          <SheetHeader><SheetTitle className="font-heading">Coverage & Engineers — {manageP?.name}</SheetTitle></SheetHeader>
          <div className="py-6 space-y-6">
            <div>
              <Label className="label-caps">Add Serviceable Pincodes</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">Enter pincodes separated by comma/space. City & state are auto-filled from the pincode table.</p>
              <div className="flex gap-2">
                <Input value={pinInput} onChange={(e) => setPinInput(e.target.value)} placeholder="400101, 411001" className="rounded-sm" data-testid="manage-pincode-input" />
                <Button className="rounded-sm" onClick={addPincodes} disabled={manageBusy} data-testid="manage-add-pincode">{manageBusy ? "…" : "Add"}</Button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={downloadPinTemplate} data-testid="pincode-template-btn">
                  <DownloadSimple size={14} /> Template
                </Button>
                <label>
                  <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importPincodes} data-testid="pincode-import-input" />
                  <Button asChild variant="outline" size="sm" className="rounded-sm gap-1.5" disabled={manageBusy}>
                    <span data-testid="pincode-import-btn" className="cursor-pointer"><UploadSimple size={14} /> {manageBusy ? "Importing…" : "Import Excel"}</span>
                  </Button>
                </label>
                <span className="text-xs text-muted-foreground">Bulk upload pincodes for this partner</span>
              </div>
            </div>

            <div>
              <Label className="label-caps mb-2 block">Current Service Areas</Label>
              {(!manageP?.service_areas || manageP.service_areas.length === 0) ? (
                <p className="text-sm text-muted-foreground">No pincodes assigned yet.</p>
              ) : (
                <div className="space-y-3" data-testid="manage-areas">
                  {manageP.service_areas.map((a, i) => (
                    <div key={i}>
                      <p className="text-xs font-semibold text-muted-foreground mb-1.5">{a.city || "Other"}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(a.pincodes || []).map((pc, j) => {
                          const pin = pc.pincode || pc;
                          return (
                            <span key={j} className="inline-flex items-center gap-1 text-xs font-mono bg-secondary px-2 py-1 rounded-sm">
                              {pin}
                              <button onClick={() => removePincode(pin)} className="text-destructive hover:text-destructive/70" data-testid={`remove-pin-${pin}`}><Trash size={11} /></button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-border pt-5">
              <Label className="label-caps flex items-center gap-1.5 mb-2"><UsersThree size={15} /> Engineer Details</Label>
              {engs.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="manage-engineers">No engineers added by this partner yet.</p>
              ) : (
                <div className="border border-border rounded-sm overflow-hidden" data-testid="manage-engineers">
                  <Table>
                    <TableHeader><TableRow className="bg-secondary/50">
                      <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Pincode</TableHead><TableHead>City/State</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {engs.map((e) => (
                        <TableRow key={e.id} data-testid={`manage-eng-${e.id}`}>
                          <TableCell className="font-semibold">{e.name}</TableCell>
                          <TableCell className="text-xs"><div>{e.phone}</div><div className="text-muted-foreground">{e.email || "—"}</div></TableCell>
                          <TableCell className="font-mono text-xs">{e.pincode}</TableCell>
                          <TableCell className="text-sm">{e.city}{e.state ? `, ${e.state}` : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {walletP && <WalletSheet partner={walletP} isAdmin={isAdmin} onClose={() => { setWalletP(null); load(); }} />}
    </>
  );
}

function WalletSheet({ partner, isAdmin, onClose }) {
  const [w, setW] = useState(null);
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("deposit");
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get(`/partners/${partner.id}/wallet`); setW(data); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const submit = async () => {
    if (!amt) { toast.error("Enter amount"); return; }
    setBusy(true);
    try {
      await api.post(`/partners/${partner.id}/wallet/${mode}`, { amount: Number(amt), note });
      toast.success(mode === "deposit" ? "Deposit added" : "Adjustment recorded");
      setAmt(""); setNote(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Sheet open={true} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="wallet-sheet">
        <SheetHeader><SheetTitle className="font-heading">{partner.name} — Deposit Wallet</SheetTitle></SheetHeader>
        {w && (
          <div className="py-5 space-y-5">
            <div className={`rounded-sm p-4 ${w.low ? "bg-red-50 border border-red-200" : "bg-secondary"}`} data-testid="wallet-balance">
              <p className="label-caps">Deposit balance</p>
              <p className="font-heading text-3xl font-bold mt-1">₹{Number(w.balance).toLocaleString("en-IN")}</p>
              <p className="text-xs text-muted-foreground mt-1">Fee: {w.fee_type === "percent" ? `${w.fee_value}% of price` : `₹${w.fee_value} / pickup`} · Low-balance threshold ₹{w.low_balance_threshold}</p>
              {w.low && <p className="text-xs text-red-600 font-semibold mt-1">⚠ Below threshold — auto-allocation paused.</p>}
            </div>
            <div className="border border-border rounded-sm p-3 space-y-3">
              <div className="flex gap-2">
                <Button size="sm" variant={mode === "deposit" ? "default" : "outline"} className="rounded-sm" onClick={() => setMode("deposit")} data-testid="wallet-mode-deposit">Add Deposit</Button>
                {isAdmin && <Button size="sm" variant={mode === "adjust" ? "default" : "outline"} className="rounded-sm" onClick={() => setMode("adjust")} data-testid="wallet-mode-adjust">Adjustment (+/-)</Button>}
              </div>
              <Input type="number" placeholder={mode === "adjust" ? "Amount (use - to deduct)" : "Deposit amount ₹"} className="rounded-sm" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="wallet-amount" />
              <Input placeholder="Note (optional)" className="rounded-sm" value={note} onChange={(e) => setNote(e.target.value)} data-testid="wallet-note" />
              <Button className="rounded-sm w-full" onClick={submit} disabled={busy} data-testid="wallet-submit">Record</Button>
            </div>
            <div>
              <p className="label-caps mb-2">Ledger</p>
              <div className="space-y-1.5" data-testid="wallet-ledger">
                {(w.ledger || []).length === 0 && <p className="text-xs text-muted-foreground">No transactions yet</p>}
                {(w.ledger || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-xs border-b border-border pb-1.5">
                    <div><span className="font-medium capitalize">{l.type}</span><div className="text-muted-foreground">{l.note} · {(l.at || "").slice(0, 10)}</div></div>
                    <div className="text-right"><span className={l.amount < 0 ? "text-red-600 font-semibold" : "text-green-700 font-semibold"}>{l.amount < 0 ? "-" : "+"}₹{Math.abs(l.amount)}</span><div className="text-muted-foreground">bal ₹{l.balance_after}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

